import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Context Providers
import { CartProvider, WishlistProvider, QuoteCartProvider } from './context';

// Modern Pages
import Layout from './modern-components/layout/Layout';
import LandingPage from './modern-pages/Home';
import Products from './modern-pages/Products';
import ProductDetails from './modern-pages/ProductDetails';
import ModernRequestQuote from './modern-pages/RequestQuote';
import About from './modern-pages/About';
import Contact from './modern-pages/Contact';
import NotFound from './modern-pages/NotFound';
import Wishlist from './modern-pages/Wishlist';
import OrderTracking from './modern-pages/OrderTracking';
import Checkout from './modern-pages/Checkout';
import PrivacyPolicy from './modern-pages/PrivacyPolicy';
import TermsConditions from './modern-pages/TermsConditions';
import ReturnRefund from './modern-pages/ReturnRefund';
import DeleteAccount from './modern-pages/DeleteAccount';
import BrandProducts from './modern-pages/BrandProducts';
import QuoteCart from './modern-pages/QuoteCart';

// Old Pages (for backward compatibility)
import Login from './pages/Login';
import Register from './pages/Register';
import Home from './pages/Home';
import OldNewHome from './pages/NewHome';
import OldAbout from './pages/About';
import ProductCatalog from './pages/ProductCatalog';
import OldProductDetails from './pages/ProductDetails';
import RequestQuote from './pages/RequestQuote';
import Quotes from './pages/Quotes';
import PlaceOrder from './pages/PlaceOrder';
import Orders from './pages/Orders';
import Finance from './pages/Finance';
import Tenders from './pages/Tenders';
import Notifications from './pages/Notifications';

// Admin Pages
import AdminLogin from './pages/AdminLogin';
import AdminDashboard from './pages/AdminDashboard';

const theme = createTheme({
  palette: {
    primary: {
      main: '#006efa',
    },
    secondary: {
      main: '#07cdbe',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
    },
    h2: {
      fontWeight: 700,
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 8,
          fontWeight: 600,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <CartProvider>
        <WishlistProvider>
          <QuoteCartProvider>
          <Router>
            <Routes>
              {/* Auth Routes (without layout) */}
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/dashboard" element={<Home />} />

              {/* Modern Routes with Layout */}
              <Route element={<Layout />}>
                <Route path="/" element={<LandingPage />} />
                <Route path="/products" element={<Products />} />
                <Route path="/products/:id" element={<ProductDetails />} />
                <Route path="/brands/:id" element={<BrandProducts />} />
                <Route path="/get-quote" element={<ModernRequestQuote />} />
                <Route path="/about" element={<About />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/quote-cart" element={<QuoteCart />} />
                <Route path="/checkout" element={<Checkout />} />
                <Route path="/wishlist" element={<Wishlist />} />
                <Route path="/track-order/:orderId" element={<OrderTracking />} />
                <Route path="/orders/:orderId" element={<OrderTracking />} />
                <Route path="/notifications" element={<Notifications />} />
                <Route path="/orders" element={<Orders />} />
                <Route path="/quotes" element={<Quotes />} />
                <Route path="/finance" element={<Finance />} />
                <Route path="/tenders" element={<Tenders />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsConditions />} />
                <Route path="/returns" element={<ReturnRefund />} />
                <Route path="/delete-account" element={<DeleteAccount />} />
              </Route>

          {/* Old Routes (for backward compatibility - with MUI) */}
          <Route path="/old-home" element={<Home />} />
          <Route path="/old-new-home" element={<OldNewHome />} />
          <Route path="/old-about" element={<OldAbout />} />
          <Route path="/product-catalog" element={<ProductCatalog />} />
          <Route path="/old-products/:id" element={<OldProductDetails />} />
          <Route path="/request-quote" element={<RequestQuote />} />
          <Route path="/place-order" element={<PlaceOrder />} />

          {/* Admin Routes (without layout) */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin/dashboard" element={<AdminDashboard />} />

              {/* 404 Route */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Router>
          </QuoteCartProvider>
        </WishlistProvider>
      </CartProvider>
    </ThemeProvider>
  );
}

export default App;
